import React, { forwardRef, useEffect, useId, useRef } from "react";

import "../../styles/field.scss";

interface Props
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "value"> {
  /** Deliberately loose: call sites across the app hand this a null. */
  value?: any;
  label?: React.ReactNode;
  /** Sits under the field. Turns red with the rest of it when error is set. */
  helperText?: React.ReactNode;
  /** Colours the label, the border and the helper text. */
  error?: boolean;
  disabled?: boolean;
  /** Takes focus on mount, and again each time this flips back to true. */
  focus?: boolean;
  /** Goes on the outer wrapper; className goes on the textarea itself. */
  wrapperClassName?: string;
}

const Textarea = forwardRef<HTMLTextAreaElement, Props>(
  (
    {
      label,
      helperText,
      error = false,
      disabled = false,
      focus = false,
      required = false,
      rows = 4,
      className = "",
      wrapperClassName = "",
      id,
      ...rest
    },
    ref,
  ) => {
    const fieldRef = useRef<HTMLTextAreaElement | null>(null);
    const generatedId = useId();
    const fieldId = id || rest.name || generatedId;
    const helperId = `${fieldId}-helper`;

    useEffect(() => {
      if (focus && !disabled) fieldRef.current?.focus();
    }, [focus, disabled]);

    return (
      <div
        className={[
          "fld",
          "textarea-container",
          error ? "fld-error" : "",
          disabled ? "fld-disabled" : "",
          wrapperClassName,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {label && (
          <label className="fld-label" htmlFor={fieldId}>
            {label}
            {required && <span className="fld-required">*</span>}
          </label>
        )}

        <textarea
          {...rest}
          id={fieldId}
          ref={(node) => {
            fieldRef.current = node;
            if (typeof ref === "function") ref(node);
            else if (ref) ref.current = node;
          }}
          className={`fld-textarea textarea-field ${className}`.trim()}
          rows={rows}
          disabled={disabled}
          required={required}
          aria-invalid={error || undefined}
          aria-describedby={helperText ? helperId : undefined}
        />

        {helperText && (
          <div className="fld-helper" id={helperId}>
            {helperText}
          </div>
        )}
      </div>
    );
  },
);

Textarea.displayName = "Textarea";

export default Textarea;
